import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { PlanId, Subscription } from '@app/shared/+state/billing';
import { isSimplifiedError, NotificationType } from '@app/shared/interfaces';
import { PaymentCard, PaymentCardStatus } from '@app/shared/models/payment-card';
import { BillingService } from '@app/shared/services/billing/billing.service';
import { CountriesService } from '@app/shared/services/countries.service';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import {
  AdditionalData,
  Component as ChargebeeCardComponent,
  ChargebeeInstance,
  InitOptions,
  PaymentIntent,
  PaymentOptions,
} from '@chargebee/chargebee-js-types';
import { PaymentMethodType } from '@chargebee/chargebee-js-types/enums';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  from,
  map,
  ReplaySubject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from '@env/environment';
const PAYPAL_SUPPORTED_CURRENCIES = [
  'AUD', // Australian dollar
  'BRL', // Brazilian real
  'CAD', // Canadian dollar
  'CNY', // Chinese Renmenbi
  'CZK', // Czech koruna
  'DKK', // Danish krone
  'EUR', // Euro
  'HKD', // Hong Kong dollar
  'HUF', // Hungarian forint
  'ILS', // Israeli new shekel
  'JPY', // Japanese yen
  'MYR', // Malaysian ringgit
  'MXN', // Mexican peso
  'TWD', // New Taiwan dollar
  'NZD', // New Zealand dollar
  'NOK', // Norwegian krone
  'PHP', // Philippine peso
  'PLN', // Polish złoty
  'GBP', // Pound sterling
  'SGD', // Singapore dollar
  'SEK', // Swedish krona
  'CHF', // Swiss franc
  'THB', // Thai baht
  'USD', // United States dollar
];

const PAYPAL_BUTTON_OPTS = {
  style: {
    shape: 'rect',
    color: 'white',
    size: 'small',
    label: 'paypal',
    tagline: 'false',
  },
};

type ChargebeeHandlePayment = (
  paymentMethodType?: PaymentMethodType,
  paymentOptions?: PaymentOptions
) => Promise<PaymentIntent>;

declare let Chargebee: {
  init(op: InitOptions): void;
  getInstance(): ChargebeeInstance;
};

@Component({
  standalone: false,
  selector: 'sbnb-payment-cards',
  templateUrl: './payment-cards.component.html',
  styleUrls: ['./payment-cards.component.scss'],
})
export class PaymentCardsComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  public PaymentCardStatus = PaymentCardStatus;
  loading$ = new BehaviorSubject(true);
  savedCards: PaymentCard[];
  cardComponent?: ChargebeeCardComponent = null;
  error = '';
  chargeBeeToken: string;
  saving = false;
  paymentIntent: any;
  addCardSaveEnabled = false;
  primaryCard: PaymentCard;
  environment = environment;
  public currentSubscription$ = new BehaviorSubject<Subscription>(null);
  public unsupportedCurrency = false;

  user = this.auth.getUserDetails();

  @Input() embedded = false;
  @Input() canDeleteLastPaymentMethod = false;
  @Input() planId?: PlanId;

  @Output() saved: EventEmitter<{
    type: 'paypal' | 'card';
    token: string;
  }> = new EventEmitter();

  constructor(
    private billingService: BillingService,
    private toast: ToastNotificationsService,
    private auth: AuthenticationService,
    private segment: SegmentIoService
  ) {}

  ngOnInit() {}

  public ngAfterViewInit() {
    this.billingService
      .initBilling$(this.planId)
      .pipe(tap(() => this.loading$.next(false)))
      .subscribe(([subscriptionDetails, paymentCards]) => {
        if (isSimplifiedError(subscriptionDetails)) {
          this.toast.open(`An error occurred: ${subscriptionDetails.error}`, 'Dismiss', NotificationType.Error);
          return EMPTY;
        }
        this.currentSubscription$.next(subscriptionDetails);
        const currentCurrency = subscriptionDetails.plan?.current_amount?.currency;
        this.unsupportedCurrency = currentCurrency && !PAYPAL_SUPPORTED_CURRENCIES.includes(currentCurrency);
        this.updateCards(paymentCards);
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadPaypal() {
    combineLatest([this.billingService.getPaymentIntent('paypal', this.planId), Chargebee.getInstance().load('paypal')])
      .pipe(
        tap(([initialPaymentIntent, paypalHandler]) => {
          this.paymentIntent = initialPaymentIntent;
          paypalHandler.setPaymentIntent(initialPaymentIntent, null);
        }),
        switchMap(([, paypalHandler]) =>
          from(paypalHandler.mountPaymentButton('#paypal-button', PAYPAL_BUTTON_OPTS)).pipe(map(() => paypalHandler))
        ),
        // creates a promise that resolves when the user adds a paypal card
        switchMap((paypalHandler: { handlePayment: ChargebeeHandlePayment }) => paypalHandler.handlePayment()),
        map((paymentIntent: PaymentIntent) => this.handleAddPaypalCard(paymentIntent)),
        takeUntil(this.destroyed$),
        catchError((error) => {
          console.error(error);
          this.toast.open(`An error occurred: ${error}`, 'Dismiss', NotificationType.Error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private handleAddPaypalCard(paymentIntent: PaymentIntent) {
    if (this.embedded) {
      this.saving = false;
      this.saved.emit({
        type: 'card',
        token: paymentIntent.id,
      });
      return;
    }

    this.billingService.addCard(paymentIntent.id).subscribe((res) => {
      this.saving = false;

      if (res.error || !res.data?.id) {
        this.toast.open(`${res.message}`, 'Dismiss', NotificationType.Error);
        return;
      }

      this.saved.emit();
      this.fetchPaymentCards();
    });
  }

  // Chargebee
  onReady(cardComponent: ChargebeeCardComponent) {
    this.cardComponent = cardComponent;
  }

  onChange(status) {
    this.addCardSaveEnabled = true;
    this.error = status.error && status.error.message ? status.error.message : '';
  }

  fetchPaymentCards() {
    this.saving = false;
    this.addCardSaveEnabled = false;

    this.loading$.next(true);
    this.billingService
      .fetchPaymentCards()
      .pipe(tap(() => this.loading$.next(false)))
      .subscribe((res) => {
        this.updateCards(res);
      });
  }

  private updateCards(paymentCards: PaymentCard[]) {
    this.savedCards = paymentCards;
    this.primaryCard = paymentCards.find((card) => card.primary === true);
    this.loadPaypal();
  }

  changePaymentMethodRole(card: PaymentCard, role: string) {
    card.actioning = true;
    this.billingService.changePaymentMethodRole(card.id, role).subscribe((res: any) => {
      if (!res.error) {
        this.fetchPaymentCards();
      } else {
        card.actioning = false;

        this.toast.open(
          `There was an error changing the role for this payment method`,
          'Dismiss',
          NotificationType.Error
        );
      }
    });
  }

  deleteCard(card: PaymentCard) {
    card.actioning = true;
    this.billingService.deleteCard(card.id).subscribe((res: any) => {
      if (res.success) {
        this.fetchPaymentCards();
      } else {
        card.actioning = false;

        this.toast.open(`${res.message}`, 'Dismiss', NotificationType.Error);
      }
    });
  }

  getIntent() {
    this.billingService.getPaymentIntent('card', this.planId).subscribe((res) => {
      this.paymentIntent = res;
      this.authorize3ds();
    });
  }

  authorize3ds() {
    if (!this.paymentIntent) {
      alert('no payment intent detected, please retry or contact support');
      return;
    }

    const additional3dsData = this.additional3dsData();

    this.segment.track(SegmentEvent.DirectPaymentCardsAuthorize3dsStarted, {});

    this.cardComponent
      .authorizeWith3ds(this.paymentIntent, additional3dsData, undefined)
      .then((intent) => {
        // Tracking the status of 3DS authorization to help us ensure we're sending the right data and not being rejected
        this.segment.track(SegmentEvent.DirectPaymentCardsAuthorize3dsReturned, {
          status: intent.status,
          active_payment_attempt_status: intent.active_payment_attempt?.status,
        });

        if (!this.embedded) {
          this.billingService.addCard(intent.id).subscribe((res) => {
            if (res.data && res.data.id) {
              this.saved.emit();
              this.fetchPaymentCards();
            } else {
              if (res.error) {
                this.toast.open(`${res.message}`, 'Dismiss', NotificationType.Error);
              }
              this.saving = false;
            }
          });
        } else {
          this.saving = false;
          this.saved.emit({
            type: 'card',
            token: intent.id,
          });
        }
      })
      .catch((error) => {
        this.segment.track(SegmentEvent.DirectPaymentCardsAuthorize3dsFailed, {
          error: error,
        });
        this.error = error.message;
        this.toast.open(`An error occurred: ${this.error}`, 'Dismiss', NotificationType.Error);

        this.paymentIntent = null;
        this.saving = false;
      });
  }

  saveChangesClicked() {
    this.saving = true;

    this.getIntent();
  }

  continue() {
    this.saved.emit();
  }

  public get noBackupCard() {
    return this.savedCards?.every?.((card) => !card.backup);
  }

  private additional3dsData(): AdditionalData {
    const billingAddress = {
      firstName: this.user.first_name,
      lastName: this.user.last_name,
      phone: this.user.phone,
    };

    return {
      email: this.user.email,
      phone: this.user.phone,
      billingAddress,
      customerBillingAddress: billingAddress,
    };
  }
}
